import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FAQ_DATA } from '../data/homepageData';

/**
 * FAQ accordion section.
 * One item open at a time. Uses framer-motion AnimatePresence for
 * smooth height animation instead of CSS max-height tricks.
 */
const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section
      id="faq"
      className="relative"
      style={{ padding: 'clamp(64px, 10vw, 140px) 0' }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <div className="text-center">
          <div
            className="inline-flex items-center gap-2 mb-4"
            style={{
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--cv-accent)',
            }}
          >
            <span style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }} />
            FAQ
            <span style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }} />
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-extrabold tracking-tight"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-2xl)',
              letterSpacing: '-0.02em',
            }}
          >
            Common questions
          </motion.h2>
        </div>

        {/* FAQ list */}
        <div
          className="mt-12 mx-auto"
          style={{ maxWidth: '740px' }}
        >
          {FAQ_DATA.map((item, i) => {
            const isOpen = openIndex === i;

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{
                  duration: 0.6,
                  delay: Math.min(i, 3) * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={{ borderBottom: '1px solid var(--cv-border)' }}
              >
                {/* Question button */}
                <button
                  className="w-full flex items-center justify-between text-left py-6 bg-transparent border-none cursor-pointer transition-colors"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-base)',
                    fontWeight: 600,
                    color: isOpen ? 'var(--cv-accent)' : 'var(--cv-text)',
                  }}
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                >
                  <span>{item.q}</span>
                  <span
                    className="cv-faq-icon flex-shrink-0 flex items-center justify-center ml-4"
                    style={{
                      width: '24px',
                      height: '24px',
                      fontSize: '20px',
                      color: isOpen ? 'var(--cv-accent)' : 'var(--cv-text-faint)',
                      transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), color 0.3s',
                    }}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </button>

                {/* Answer — animated via AnimatePresence */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <p
                        className="pb-6"
                        style={{
                          fontSize: 'var(--text-sm)',
                          color: 'var(--cv-text-muted)',
                          lineHeight: 1.7,
                        }}
                      >
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
